import React, { useState, useRef, useEffect, useCallback } from "react";
import { Fab, useTheme, Tooltip } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

const DraggableFab = ({
  onClick,
  initialPosition = { bottom: 20, right: 20 },
  icon = <FilterListIcon />,
  children,
  color = "primary",
  tooltipTitle = "Filters",
  ...props
}) => {
  const theme = useTheme();
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragMoved, setDragMoved] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const fabRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const positionStartRef = useRef({ x: 0, y: 0 });
  const dragTimeoutRef = useRef(null);

  // Load saved position on mount
  useEffect(() => {
    try {
      const savedPosition = localStorage.getItem("fabPosition");
      if (savedPosition) {
        const parsed = JSON.parse(savedPosition);
        // Validate position is within reasonable bounds
        if (
          parsed.right >= 10 &&
          parsed.bottom >= 10 &&
          parsed.right <= window.innerWidth - 60 &&
          parsed.bottom <= window.innerHeight - 60
        ) {
          setPosition(parsed);
        }
      }
    } catch (e) {
      console.warn("Failed to parse saved FAB position", e);
    }
  }, []);

  // Save position when dragging stops
  useEffect(() => {
    if (!isDragging && dragMoved) {
      const timeoutId = setTimeout(() => {
        try {
          localStorage.setItem("fabPosition", JSON.stringify(position));
        } catch (e) {
          console.warn("Failed to save FAB position", e);
        }
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [position, isDragging, dragMoved]);

  const startDrag = useCallback((clientX, clientY) => {
    // Clear any existing timeout
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }

    // Add delay before starting drag to prevent accidental drags
    dragTimeoutRef.current = setTimeout(() => {
      setIsDragging(true);
      setDragMoved(false);

      dragStartRef.current = { x: clientX, y: clientY };

      if (fabRef.current) {
        const fabRect = fabRef.current.getBoundingClientRect();
        positionStartRef.current = {
          x: window.innerWidth - fabRect.right,
          y: window.innerHeight - fabRect.bottom,
        };
      }
    }, 150); // 150ms delay before drag starts
  }, []);

  const handleDrag = useCallback(
    (clientX, clientY) => {
      if (!isDragging) return;

      const deltaX = clientX - dragStartRef.current.x;
      const deltaY = dragStartRef.current.y - clientY; // Invert Y for bottom positioning

      // Mark as moved if significant movement
      if (!dragMoved && (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8)) {
        setDragMoved(true);
      }

      // Calculate new position with better bounds checking
      const fabSize = 56; // Standard FAB size
      const margin = 16;

      const newRight = Math.max(
        margin,
        Math.min(
          window.innerWidth - fabSize - margin,
          positionStartRef.current.x + deltaX
        )
      );

      const newBottom = Math.max(
        margin,
        Math.min(
          window.innerHeight - fabSize - margin,
          positionStartRef.current.y + deltaY
        )
      );

      setPosition({ right: newRight, bottom: newBottom });
    },
    [isDragging, dragMoved]
  );

  const endDrag = useCallback(() => {
    // Clear timeout if drag ends before delay
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }

    setIsDragging(false);

    // Reset drag moved after a short delay to allow click detection
    setTimeout(() => {
      setDragMoved(false);
    }, 100);
  }, []);

  // Mouse events
  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      startDrag(e.clientX, e.clientY);
    },
    [startDrag]
  );

  const handleMouseMove = useCallback(
    (e) => {
      handleDrag(e.clientX, e.clientY);
    },
    [handleDrag]
  );

  // Touch events
  const handleTouchStart = useCallback(
    (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      startDrag(touch.clientX, touch.clientY);
    },
    [startDrag]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (isDragging) {
        e.preventDefault();
        const touch = e.touches[0];
        handleDrag(touch.clientX, touch.clientY);
      }
    },
    [isDragging, handleDrag]
  );

  // Global event listeners
  useEffect(() => {
    if (isDragging) {
      const handleMouseMoveGlobal = (e) => handleMouseMove(e);
      const handleTouchMoveGlobal = (e) => handleTouchMove(e);

      document.addEventListener("mousemove", handleMouseMoveGlobal);
      document.addEventListener("mouseup", endDrag);
      document.addEventListener("touchmove", handleTouchMoveGlobal, {
        passive: false,
      });
      document.addEventListener("touchend", endDrag);

      return () => {
        document.removeEventListener("mousemove", handleMouseMoveGlobal);
        document.removeEventListener("mouseup", endDrag);
        document.removeEventListener("touchmove", handleTouchMoveGlobal);
        document.removeEventListener("touchend", endDrag);
      };
    }
  }, [isDragging, handleMouseMove, handleTouchMove, endDrag]);

  const handleClick = useCallback(
    (e) => {
      // Only trigger onClick if we didn't drag
      if (!dragMoved && onClick) {
        onClick(e);
      }
    },
    [onClick, dragMoved]
  );

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  return (
    <Tooltip title={tooltipTitle} placement="left">
      <Fab
        ref={fabRef}
        color={color}
        aria-label={`${tooltipTitle} - draggable`}
        variant={children ? "extended" : "circular"}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: "fixed",
          ...position,
          zIndex: theme.zIndex.speedDial,
          cursor: isDragging ? "grabbing" : "pointer",
          transition: isDragging
            ? "none"
            : theme.transitions.create(
                ["background-color", "box-shadow", "transform", "opacity"],
                { duration: theme.transitions.duration.short }
              ),
          display: "flex",
          alignItems: "center",
          gap: 1,
          userSelect: "none",
          touchAction: "none",
          minHeight: 56,
          minWidth: 56,
          opacity: isDragging ? 0.8 : 1,
          boxShadow: isDragging
            ? theme.shadows[12]
            : isHovered
            ? theme.shadows[8]
            : theme.shadows[6],
          "&:hover": {
            boxShadow: theme.shadows[8],
            transform: isDragging ? "none" : "scale(1.05)",
          },
          "&:active": !isDragging && {
            transform: "scale(0.95)",
          },
          // Add visual feedback for draggable state
          "&::before": {
            content: '""',
            position: "absolute",
            top: -2,
            left: -2,
            right: -2,
            bottom: -2,
            borderRadius: "inherit",
            background: `linear-gradient(45deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
            opacity: isHovered ? 0.3 : 0,
            transition: theme.transitions.create("opacity"),
            zIndex: -1,
          },
          ...props.sx,
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        {...props}
      >
        {/* Drag indicator - only show on hover for cleaner look */}
        {isHovered && !isDragging && (
          <DragIndicatorIcon
            fontSize="small"
            sx={{
              opacity: 0.6,
              transition: theme.transitions.create("opacity"),
            }}
          />
        )}

        {icon}

        {children && (
          <span
            style={{
              marginLeft: isHovered ? 4 : 8,
              transition: theme.transitions.create("margin-left"),
            }}
          >
            {children}
          </span>
        )}
      </Fab>
    </Tooltip>
  );
};

export default DraggableFab;
